import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PORTAL, usePortal } from '../portalAuth';
import { IMG } from '../components/StoreLayout';
import { STORE_BASE } from '../routeBase';

/// B2B "paste your SKU list" bulk-add — a distributor reordering 30+ known
/// model numbers at once shouldn't have to search and click through the
/// catalogue one product at a time. Same POST /api/portal/products/bulk
/// endpoint the mobile app's own Bulk Order screen already uses, and the
/// same "MODEL" / "MODEL,QTY" / "MODEL QTY" per-line paste format. A private
/// account page (like Wishlist/Orders) — no useSEO call, same as those.
export default function BulkOrder() {
  const { customer, setCart, ready } = usePortal();

  const [text, setText] = useState('');
  const [matched, setMatched] = useState([]);
  const [notFound, setNotFound] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [searched, setSearched] = useState(false);

  // Accepts "MODEL", "MODEL,QTY", or "MODEL QTY" per line — same parsing
  // rule as the mobile app's BulkOrderScreen._parseInput.
  const parseInput = raw => {
    const result = {};
    for (const rawLine of raw.split('\n')) {
      const line = rawLine.trim();
      if (!line) continue;
      const parts = line.split(/[,\s]+/);
      const model = (parts[0] || '').trim();
      if (!model) continue;
      const qty = parts.length > 1 ? parseInt(parts[1], 10) : 1;
      result[model.toLowerCase()] = Number.isFinite(qty) && qty > 0 ? qty : 1;
    }
    return result;
  };

  const entries = useMemo(() => parseInput(text), [text]);
  const pastedLines = Object.keys(entries).length;
  const pastedUnits = Object.values(entries).reduce((s, q) => s + q, 0);

  const findProducts = async () => {
    const modelNos = Object.keys(entries);
    if (!modelNos.length) { setError('Paste at least one model number, one per line.'); return; }
    setLoading(true); setError(''); setSearched(true);
    try {
      const r = await PORTAL.post('/products/bulk', { model_nos: modelNos });
      const products = r.data.products || [];
      setMatched(products);
      setNotFound(r.data.not_found || []);
      const q = {};
      products.forEach(p => { q[p.model_no] = entries[p.model_no.toLowerCase()] || 1; });
      setQuantities(q);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not look up those models.');
    }
    setLoading(false);
  };

  const setQty = (modelNo, raw) => {
    const qty = parseInt(raw, 10);
    setQuantities(q => ({ ...q, [modelNo]: Number.isFinite(qty) && qty > 0 ? qty : q[modelNo] }));
  };

  const matchedUnits = matched.reduce((s, p) => s + (quantities[p.model_no] || 1), 0);
  const matchedTotal = matched.reduce((s, p) => s + (p.price != null ? p.price * (quantities[p.model_no] || 1) : 0), 0);

  const addAllToCart = async () => {
    setAdding(true);
    let added = 0, lastCart = null;
    for (const p of matched) {
      if (!p.variant_id) continue;
      try {
        const r = await PORTAL.post('/cart', { variant_id: p.variant_id, qty: quantities[p.model_no] || 1 });
        lastCart = r.data;
        added++;
      } catch (e) { /* keep going — one bad line shouldn't stop the rest */ }
    }
    if (lastCart) setCart(lastCart);
    setAdding(false);
    if (added) {
      setText(''); setMatched([]); setNotFound([]); setQuantities({}); setSearched(false);
      window.location.href = `${STORE_BASE}/cart`;
    } else {
      setError('Could not add these items to your cart — please try again.');
    }
  };

  if (!ready) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center', padding: 20 }}>
      <i className="ti ti-list-details" style={{ fontSize: 44, color: '#ddd' }} />
      <h2 style={{ marginTop: 12 }}>Please sign in to place a bulk order</h2>
      <p style={{ color: '#888', fontSize: 13.5, margin: '6px 0 0' }}>Bulk ordering is a wholesale account feature.</p>
      <Link to={`${STORE_BASE}/login`} style={btn}>Sign in</Link>
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '24px auto', padding: '0 24px 80px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 6px' }}>Bulk Order</h1>
      <p style={{ color: '#888', fontSize: 13.5, margin: '0 0 20px', lineHeight: 1.6 }}>
        Reordering a known list of models? Paste them below — one per line — instead of searching the
        catalogue one at a time. Add a quantity after each model if you need more than 1
        (e.g. <code>46810 25</code> or <code>46810,25</code>).
      </p>

      <div style={{ position: 'relative' }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={'46810, 24\n46809, 12\n42194, 48'}
          rows={7}
          style={{
            width: '100%', boxSizing: 'border-box', border: '1.5px dashed #ccc', borderRadius: 12,
            padding: '14px 14px 26px', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 13,
            resize: 'vertical', outline: 'none',
          }}
        />
        {pastedLines > 0 && (
          <span style={{ position: 'absolute', right: 14, bottom: 10, fontSize: 11, color: '#999' }}>
            {pastedLines} line{pastedLines === 1 ? '' : 's'} · {pastedUnits} unit{pastedUnits === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 10 }}>{error}</div>}

      <button onClick={findProducts} disabled={loading} className="sf-btn-primary" style={{ ...btnPrimary, marginTop: 14 }}>
        {loading ? 'Searching…' : 'Find products'}
      </button>

      {notFound.length > 0 && (
        <div style={{ background: '#fffbeb', color: '#9a6a00', fontSize: 13, padding: '10px 14px', borderRadius: 8, marginTop: 18 }}>
          Couldn't find: {notFound.join(', ')}
        </div>
      )}

      {searched && !loading && matched.length === 0 && notFound.length === 0 && (
        <div style={{ fontSize: 13, color: '#888', marginTop: 18 }}>No matching models found — check the numbers and try again.</div>
      )}

      {matched.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '24px 0 10px' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
              {notFound.length === 0 ? `Matched · ${matched.length}` : `Matched · ${matched.length} of ${matched.length + notFound.length}`}
            </h3>
            {notFound.length > 0 && (
              <span style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 600 }}>{notFound.length} unmatched</span>
            )}
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
            {matched.map((p, i) => {
              const qty = quantities[p.model_no] || 1;
              const lineTotal = p.price != null ? p.price * qty : null;
              return (
                <div key={p.model_no} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderTop: i === 0 ? 'none' : '1px solid #f2f2f2',
                }}>
                  <Link to={`${STORE_BASE}/product/${encodeURIComponent(p.model_no)}`} style={{ flexShrink: 0 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 8, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      {p.image
                        ? <img src={IMG(p.image)} alt="" loading="lazy" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        : <i className="ti ti-eyeglass-2" style={{ color: '#ddd', fontSize: 20 }} />}
                    </div>
                  </Link>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`${STORE_BASE}/product/${encodeURIComponent(p.model_no)}`} style={{ fontWeight: 600, fontSize: 13.5, color: 'inherit', textDecoration: 'none' }}>
                      {p.model_no}
                    </Link>
                    {p.price != null && (
                      <div style={{ fontSize: 11.5, color: '#888', marginTop: 2 }}>${Number(p.price).toFixed(2)} each</div>
                    )}
                  </div>
                  <input
                    type="number" min="1" value={qty}
                    onChange={e => setQty(p.model_no, e.target.value)}
                    style={{ width: 52, textAlign: 'center', padding: '7px 4px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit' }}
                  />
                  {lineTotal != null && (
                    <div style={{ width: 64, textAlign: 'right', fontWeight: 700, fontSize: 12.5, color: 'var(--sf-primary)' }}>
                      {lineTotal.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{
            position: 'sticky', bottom: 0, marginTop: 20, background: '#fff', border: '1px solid #eee',
            borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
          }}>
            <div>
              <div style={{ fontSize: 11.5, color: '#888' }}>
                {matched.length} item{matched.length === 1 ? '' : 's'} · {matchedUnits} unit{matchedUnits === 1 ? '' : 's'}
              </div>
              {matchedTotal > 0 && <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--sf-primary)' }}>${matchedTotal.toFixed(2)}</div>}
            </div>
            <button onClick={addAllToCart} disabled={adding} className="sf-btn-primary" style={btnPrimary}>
              {adding ? 'Adding…' : 'Add all to cart'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const btn = { display: 'inline-block', marginTop: 18, padding: '10px 24px', borderRadius: 22,
  border: '1.5px solid var(--sf-accent)', textDecoration: 'none', fontSize: 14, fontWeight: 600, color: 'inherit' };
const btnPrimary = { padding: '11px 22px', borderRadius: 8, border: 'none', background: 'var(--sf-primary)',
  color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };
